/**
 * Impersonation consent page (public, from the email link) -- LAU F2a.
 *
 * The support-consent email points here with ?token=... This page is the
 * missing frontend that consumes the existing backend routes:
 *   POST /api/auth/impersonation-consent/inspect   { token } -> { reason, duration_seconds }
 *   POST /api/auth/impersonation-consent/respond   { token, decision }
 *
 * Public by design: the user arrives from their inbox, not a logged-in
 * session. The token is the bearer of authority; the server rate-limits
 * by IP and returns a generic "invalid_or_expired" envelope (V481) so the
 * page can never be used as an oracle. No account data is shown beyond the
 * admin-supplied reason and the duration.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ShieldCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";

type Phase = "loading" | "ready" | "done_approved" | "done_declined" | "error";

export default function ImpersonationConsentPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [phase, setPhase] = useState<Phase>("loading");
  const [reason, setReason] = useState("");
  const [durationMin, setDurationMin] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (token.length < 20) {
        setPhase("error");
        return;
      }
      try {
        const res = await fetch("/api/auth/impersonation-consent/inspect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!alive) return;
        if (!res.ok) {
          setPhase("error");
          return;
        }
        const data = (await res.json()) as {
          reason: string;
          duration_seconds: number;
        };
        setReason(data.reason);
        setDurationMin(Math.round(data.duration_seconds / 60));
        setPhase("ready");
      } catch {
        if (alive) setPhase("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const respond = async (decision: "approve" | "decline") => {
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/impersonation-consent/respond", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, decision }),
      });
      if (!res.ok) {
        setPhase("error");
        return;
      }
      setPhase(decision === "approve" ? "done_approved" : "done_declined");
    } catch {
      setPhase("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-sm ring-1 ring-zinc-900/[0.04]">
        <div className="flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900">
            <ShieldCheck className="h-6 w-6 text-white" strokeWidth={2} />
          </div>
        </div>

        {phase === "loading" ? (
          <div className="mt-6 flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
            <p className="text-[14px] text-zinc-500">
              {t("consentPage.loading")}
            </p>
          </div>
        ) : null}

        {phase === "error" ? (
          <div className="mt-6 text-center">
            <h1 className="text-[20px] font-semibold text-zinc-900">
              {t("consentPage.error_title")}
            </h1>
            <p className="mt-2 text-[14px] text-zinc-500">
              {t("consentPage.error_body")}
            </p>
          </div>
        ) : null}

        {phase === "ready" ? (
          <div className="mt-6">
            <h1 className="text-center text-[20px] font-semibold text-zinc-900">
              {t("consentPage.title")}
            </h1>
            <p className="mt-2 text-center text-[14px] leading-relaxed text-zinc-500">
              {t("consentPage.lead", { minutes: durationMin })}
            </p>

            <div className="mt-5 rounded-2xl bg-zinc-100/70 px-4 py-3">
              <p className="text-[13px] text-zinc-600">
                <strong className="font-semibold text-zinc-800">
                  {t("consentPage.reason_label")}:
                </strong>{" "}
                {reason}
              </p>
            </div>

            <p className="mt-4 text-[13px] leading-relaxed text-zinc-500">
              {t("consentPage.explainer")}
            </p>

            <button
              type="button"
              onClick={() => respond("approve")}
              disabled={submitting}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-zinc-900 px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-zinc-700 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              {t("consentPage.approve")}
            </button>
            <button
              type="button"
              onClick={() => respond("decline")}
              disabled={submitting}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[14px] font-semibold text-zinc-600 ring-1 ring-zinc-200 transition-colors hover:bg-zinc-50 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" />
              {t("consentPage.decline")}
            </button>
          </div>
        ) : null}

        {phase === "done_approved" ? (
          <div className="mt-6 text-center">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <h1 className="mt-3 text-[20px] font-semibold text-zinc-900">
              {t("consentPage.approved_title")}
            </h1>
            <p className="mt-2 text-[14px] text-zinc-500">
              {t("consentPage.approved_body")}
            </p>
          </div>
        ) : null}

        {phase === "done_declined" ? (
          <div className="mt-6 text-center">
            <XCircle className="mx-auto h-8 w-8 text-zinc-400" />
            <h1 className="mt-3 text-[20px] font-semibold text-zinc-900">
              {t("consentPage.declined_title")}
            </h1>
            <p className="mt-2 text-[14px] text-zinc-500">
              {t("consentPage.declined_body")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
